<script setup lang="ts">
import { computed, ref } from 'vue';
import store from '@/store';
import fileUpload from '@/shared/upload';

const { uploadStatus, handleUploadRequest } = fileUpload('stickers', 'Sticker');

const item = computed({
  get: () => store.getters['sticker/GET_STICKER'],
  set: (value) => {
    store.dispatch('sticker/SET_STICKER', value);
  },
});

const fileInput = ref<HTMLInputElement | null>(null);
const uploading = ref(false);

// Lightbox state
const lightboxItem = ref<{ type: string; url: string; thumbnail?: string | null } | null>(null);
const zoomLevel = ref(1);
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 4;
const ZOOM_STEP = 0.25;

const openLightbox = (galleryItem: { type: string; url: string; thumbnail?: string | null }) => {
  lightboxItem.value = galleryItem;
  zoomLevel.value = 1;
};

const closeLightbox = () => {
  lightboxItem.value = null;
  zoomLevel.value = 1;
};

const zoomIn = () => {
  zoomLevel.value = Math.min(MAX_ZOOM, +(zoomLevel.value + ZOOM_STEP).toFixed(2));
};

const zoomOut = () => {
  zoomLevel.value = Math.max(MIN_ZOOM, +(zoomLevel.value - ZOOM_STEP).toFixed(2));
};

const onLightboxBackdropClick = (event: MouseEvent) => {
  if ((event.target as HTMLElement).classList.contains('c-lightbox')) {
    closeLightbox();
  }
};

const updateGallery = (gallery: any[]) => {
  store.dispatch('sticker/SET_STICKER', { gallery });
};

const onAreaClick = () => {
  fileInput.value?.click();
};

const processFile = async (file: File) => {
  const isVideo = file.type.startsWith('video/');
  uploading.value = true;
  try {
    await handleUploadRequest({ file });
    if (uploadStatus.status === 'ok') {
      const current = item.value.gallery || [];
      const newItem = {
        id: null,
        type: isVideo ? 'video' : 'image',
        url: uploadStatus.resourceUrl,
        thumbnail: uploadStatus.thumbnailUrl || null,
        sortOrder: current.length,
      };
      updateGallery([...current, newItem]);
    }
  } finally {
    uploading.value = false;
  }
};

const onFileChange = async (event: Event) => {
  const target = event.target as HTMLInputElement;
  const files = target.files;
  if (!files?.length) return;
  for (let i = 0; i < files.length; i++) {
    await processFile(files[i]);
  }
  target.value = '';
};

const onDrop = async (event: DragEvent) => {
  event.preventDefault();
  const files = event.dataTransfer?.files;
  if (!files?.length) return;
  for (let i = 0; i < files.length; i++) {
    await processFile(files[i]);
  }
};

const onDragOver = (event: DragEvent) => {
  event.preventDefault();
};

const onRemoveItem = (index: number) => {
  const gallery = [...(item.value.gallery || [])];
  gallery.splice(index, 1);
  updateGallery(gallery);
};
</script>

<template lang="pug">
section.c-tab-gallery
  .c-gallery-section
    h4.c-gallery-section__title Gallery
    p.c-gallery-section__subtitle Add images and videos to showcase your event (optional)

    .c-gallery-upload(
      @click="onAreaClick"
      @drop="onDrop"
      @dragover="onDragOver"
    )
      fai(type="fal" icon="cloud-upload-alt").c-gallery-upload__icon
      p.c-gallery-upload__text(v-if="!uploading")
        strong Click to upload images or videos
      p.c-gallery-upload__text(v-else) Uploading...
      p.c-gallery-upload__hint PNG, JPG, MP4, MOV up to 50MB
      input(
        ref="fileInput"
        type="file"
        accept="image/*,video/*"
        multiple
        style="display: none"
        @change="onFileChange"
      )

  .c-gallery-grid(v-if="(item.gallery || []).length")
    .c-gallery-item(v-for="(galleryItem, i) in (item.gallery || [])" :key="i")
      .c-gallery-item__preview
        video(
          v-if="galleryItem.type === 'video'"
          :src="galleryItem.url"
          class="c-gallery-item__media"
        )
        img(
          v-else
          :src="galleryItem.thumbnail || galleryItem.url"
          :alt="'Gallery item ' + (i + 1)"
          class="c-gallery-item__media"
        )
        .c-gallery-item__overlay
          button.c-gallery-item__action(type="button" @click.stop="openLightbox(galleryItem)")
            fai(type="fal" icon="search-plus")
          button.c-gallery-item__remove(type="button" @click.stop="onRemoveItem(i)")
            fai(type="fal" icon="trash")
      .c-gallery-item__type {{ galleryItem.type }}

  teleport(to="body")
    transition(name="c-lightbox-fade")
      .c-lightbox(v-if="lightboxItem" @click="onLightboxBackdropClick")
        .c-lightbox__toolbar
          button.c-lightbox__zoom-btn(type="button" @click="zoomOut" :disabled="zoomLevel <= 0.5")
            fai(type="fal" icon="search-minus")
          span.c-lightbox__zoom-label {{ Math.round(zoomLevel * 100) }}%
          button.c-lightbox__zoom-btn(type="button" @click="zoomIn" :disabled="zoomLevel >= 4")
            fai(type="fal" icon="search-plus")
          button.c-lightbox__close(type="button" @click="closeLightbox")
            fai(type="fal" icon="times")
        .c-lightbox__stage
          video.c-lightbox__media(
            v-if="lightboxItem.type === 'video'"
            :src="lightboxItem.url"
            controls
            :style="{ transform: 'scale(' + zoomLevel + ')' }"
          )
          img.c-lightbox__media(
            v-else
            :src="lightboxItem.url"
            alt="Full size preview"
            :style="{ transform: 'scale(' + zoomLevel + ')' }"
          )
</template>

<style scoped lang="scss">
.c-tab-gallery {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.c-gallery-section {
  border: 1px solid #f0f0f0;
  border-radius: 10px;
  padding: 20px;
  background: #fff;

  &__title {
    font-size: 15px;
    font-weight: 700;
    font-family: inherit;
    color: #222;
    margin: 0 0 4px 0;
  }

  &__subtitle {
    font-size: 13px;
    color: #888;
    margin: 0 0 16px 0;
    font-family: inherit;
  }
}

.c-gallery-upload {
  width: 100%;
  min-height: 180px;
  border: 2px dashed #1ebeaf;
  border-radius: 10px;
  background: #f0fffe;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #e5fcfb;
  }

  &__icon {
    font-size: 36px;
    width: 36px;
    height: 36px;
    flex-shrink: 0;
    color: #888;
  }

  &__text {
    font-size: 14px;
    color: #333;
    margin: 0;
    font-family: inherit;

    strong {
      font-weight: 700;
    }
  }

  &__hint {
    font-size: 12px;
    color: #999;
    margin: 0;
    font-family: inherit;
  }
}

.c-gallery-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 12px;
}

.c-gallery-item {
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e8e8e8;
  background: #f5f5f5;

  &__preview {
    position: relative;
    height: 100px;

    &:hover .c-gallery-item__overlay {
      opacity: 1;
    }
  }

  &__media {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  &__overlay {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    opacity: 0;
    transition: opacity 0.2s;
  }

  &__action {
    background: rgba(255, 255, 255, 0.2);
    border: none;
    color: #fff;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 14px;
    transition: background 0.15s;

    &:hover {
      background: rgba(255, 255, 255, 0.35);
    }
  }

  &__remove {
    background: #e02020;
    border: none;
    color: #fff;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 14px;
  }

  &__type {
    padding: 4px 8px;
    font-size: 11px;
    color: #999;
    text-transform: capitalize;
    font-family: inherit;
  }
}
</style>

<style lang="scss">
.c-lightbox {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.88);
  display: flex;
  flex-direction: column;

  &__toolbar {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    padding: 12px 16px;
    flex-shrink: 0;
  }

  &__zoom-btn {
    background: rgba(255, 255, 255, 0.12);
    border: none;
    color: #fff;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 16px;
    transition: background 0.15s;

    &:hover:not(:disabled) {
      background: rgba(255, 255, 255, 0.25);
    }

    &:disabled {
      opacity: 0.35;
      cursor: not-allowed;
    }
  }

  &__zoom-label {
    color: #ddd;
    font-size: 13px;
    min-width: 46px;
    text-align: center;
    font-family: inherit;
  }

  &__close {
    background: rgba(255, 255, 255, 0.12);
    border: none;
    color: #fff;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 18px;
    margin-left: 8px;
    transition: background 0.15s;

    &:hover {
      background: rgba(220, 50, 50, 0.6);
    }
  }

  &__stage {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: auto;
    padding: 16px;
  }

  &__media {
    max-width: 90vw;
    max-height: 80vh;
    object-fit: contain;
    border-radius: 6px;
    transform-origin: center center;
    transition: transform 0.2s ease;
    display: block;
  }
}

.c-lightbox-fade-enter-active,
.c-lightbox-fade-leave-active {
  transition: opacity 0.2s ease;
}

.c-lightbox-fade-enter-from,
.c-lightbox-fade-leave-to {
  opacity: 0;
}
</style>
